import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { track } from "@/lib/pixel";
import { useAuth } from "@/context/AuthContext";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from "recharts";
import { Sparkles, ArrowRight, Loader2, Check, Lock, Target, TrendingUp, Award, Crown, Brain, RotateCcw, AlertTriangle } from "lucide-react";
import ShareReport from "@/components/ShareReport";

export default function AssessmentPage() {
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const [q, setQ] = useState(null);
  const [index, setIndex] = useState(1);
  const [confidence, setConfidence] = useState(30);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [profile, setProfile] = useState(null);
  const [initLoading, setInitLoading] = useState(true);
  const [startError, setStartError] = useState(false);

  const startFlow = useCallback(async () => {
    setStartError(false);
    setInitLoading(true);
    setProfile(null);
    setQ(null);
    setIndex(1);
    setText("");
    try {
      const r = await api.post("/assessment/adaptive/start");
      setQ(r.data.question);
      setConfidence(r.data.confidence);
    } catch {
      setStartError(true);
    } finally {
      setInitLoading(false);
    }
  }, []);

  useEffect(() => {
    // First check if the student already completed the assessment.
    (async () => {
      try {
        const { data } = await api.get("/assessment/profile");
        if (data.completed && data.profile) {
          setProfile(data.profile);
          setInitLoading(false);
          return;
        }
      } catch {
        // Network/server error — don't wipe a possibly-completed profile; show retry.
        setStartError(true);
        setInitLoading(false);
        return;
      }
      await startFlow();
    })();
  }, [startFlow]);

  const answer = async (label) => {
    if (busy || !q) return;
    setBusy(true);
    setAnalyzing(true);
    try {
      const { data } = await api.post("/assessment/adaptive/answer", { qid: q.id, answer: label });
      if (data.done) {
        setProfile(data.profile);
        await refresh();
        if (data.xp_awarded) track("Lead", { content_name: "career_assessment" }, user?._id ? `lead_${user._id}` : undefined);
        toast.success(data.xp_awarded ? "Your Career Intelligence Profile is ready! +100 XP" : "Your Career Intelligence Profile is updated!");
      } else {
        setQ(data.question); setIndex(data.index); setConfidence(data.confidence); setText("");
      }
    } catch { toast.error("Something went wrong, try again."); }
    finally { setBusy(false); setAnalyzing(false); }
  };

  if (profile) return <Result profile={profile} onGo={() => navigate("/dashboard")} onUpgrade={() => navigate("/pricing")} onRetake={startFlow} />;

  if (initLoading) return (
    <div className="min-h-screen radial-fade flex flex-col items-center justify-center gap-3 text-slate-400" data-testid="assessment-loading">
      <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
      <p>Preparing your AI assessment...</p>
    </div>
  );

  if (startError) return (
    <div className="min-h-screen radial-fade flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="assessment-error">
      <AlertTriangle className="h-10 w-10 text-amber-400" />
      <h2 className="font-display text-xl font-bold text-white">We couldn't start your assessment</h2>
      <p className="text-sm text-slate-400 max-w-sm">Please check your connection and try again.</p>
      <button data-testid="assessment-retry-btn" onClick={startFlow}
        className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3 text-sm font-bold text-[#041016]">
        <RotateCcw className="h-4 w-4" /> Retry
      </button>
    </div>
  );

  if (!q) return null;

  return (
    <div className="min-h-screen radial-fade">
      <div className="mx-auto max-w-2xl px-4 sm:px-8 py-12">
        <div className="flex items-center gap-2 mb-2">
          <Brain className="h-4 w-4 text-cyan-400" />
          <span className="font-mono-data text-xs uppercase tracking-[0.2em] text-cyan-400">Adaptive AI Assessment</span>
        </div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-slate-400">Question {index} · AI is learning about you</p>
          <p className="font-mono-data text-sm text-cyan-300">{confidence}% confident</p>
        </div>
        <div className="h-2 rounded-full bg-white/8 overflow-hidden mb-10">
          <motion.div className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400" animate={{ width: `${confidence}%` }} transition={{ duration: 0.4 }} />
        </div>

        <AnimatePresence mode="wait">
          {analyzing ? (
            <motion.div key="analyzing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="flex flex-col items-center justify-center gap-4 py-16 text-center" data-testid="assessment-analyzing">
              <div className="relative">
                <Loader2 className="h-10 w-10 animate-spin text-cyan-400" />
                <Sparkles className="absolute inset-0 m-auto h-4 w-4 text-cyan-200" />
              </div>
              <p className="font-display text-lg font-semibold text-white">AI is analyzing your answers…</p>
              <p className="text-sm text-slate-400">Building your Career Intelligence Profile. This can take a few seconds.</p>
            </motion.div>
          ) : (
            <motion.div key={q.id} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }} data-testid="assessment-question-card">
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-8">{q.text}</h2>
              {q.type === "text" ? (
                <div>
                  <textarea data-testid="assessment-text-input" rows={4} value={text} onChange={(e) => setText(e.target.value)}
                    placeholder="Type your answer..." className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4 text-sm text-white placeholder:text-slate-500 outline-none focus:border-cyan-400/50" />
                  <button data-testid="assessment-next-btn" onClick={() => answer(text || "Not sure yet")} disabled={busy}
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3 text-sm font-bold text-[#041016] disabled:opacity-50">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Finish <Sparkles className="h-4 w-4" /></>}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {q.options.map((opt) => (
                    <button key={opt} data-testid="assessment-option" onClick={() => answer(opt)} disabled={busy}
                      className="group flex w-full items-center justify-between rounded-xl border border-white/10 px-5 py-4 text-left text-sm font-medium text-slate-300 transition-all hover:border-cyan-400/50 hover:bg-cyan-400/[0.06] hover:text-white disabled:opacity-50">
                      {opt}
                      <ArrowRight className="h-4 w-4 text-slate-600 transition-all group-hover:text-cyan-400 group-hover:translate-x-1" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        {!analyzing && <p className="mt-8 text-center text-xs text-slate-500">The AI adapts each question to your previous answers — no two students get the same test.</p>}
      </div>
    </div>
  );
}

const Result = ({ profile, onGo, onUpgrade, onRetake }) => {
  const radar = (profile.dimensions || []).map((d) => ({ dim: d.name, value: d.score }));
  const unlocked = profile.free_unlocked || 3;
  const careers = profile.recommended_careers || profile.career_paths || [];
  const locked = Math.max(0, (profile.total_paths || careers.length) - unlocked);

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-8 py-12" data-testid="assessment-result">
      <div className="text-center mb-8">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-indigo-500 glow-cyan">
          <Sparkles className="h-8 w-8 text-[#041016]" />
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Your Career Intelligence Profile</h1>
        <p className="mt-3 text-slate-400 max-w-xl mx-auto">{profile.summary}</p>
        <span className="mt-3 inline-block rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-bold text-emerald-300">{profile.clarity_score}% AI confidence</span>
      </div>

      {/* Dimensions */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
          <h3 className="font-display text-lg font-semibold mb-4">Your strengths</h3>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <RadarChart data={radar} outerRadius="72%">
                <PolarGrid stroke="rgba(255,255,255,0.1)" />
                <PolarAngleAxis dataKey="dim" tick={{ fill: "#cbd5e1", fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} stroke="rgba(255,255,255,0.08)" />
                <Radar dataKey="value" stroke="#06B6D4" fill="#06B6D4" fillOpacity={0.35} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2.5">
            {(profile.dimensions || []).map((d) => (
              <div key={d.name} data-testid="dimension-score">
                <div className="flex justify-between text-sm"><span className="text-white">{d.name}</span><span className="font-mono-data text-cyan-300">{d.score}%</span></div>
                <div className="h-1.5 rounded-full bg-white/8 overflow-hidden"><div className="h-full bg-gradient-to-r from-cyan-400 to-indigo-400" style={{ width: `${d.score}%` }} /></div>
                <p className="text-xs text-slate-400 mt-1">{d.why}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Clusters */}
        <div className="rounded-2xl border border-white/8 bg-[#131C2E] p-6">
          <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2"><Target className="h-5 w-5 text-cyan-400" /> Top career clusters</h3>
          <div className="space-y-4">
            {(profile.clusters || []).slice(0, 3).map((c, i) => (
              <div key={c.name} data-testid="career-cluster">
                <div className="flex justify-between text-sm mb-1"><span className="font-medium text-white">{i + 1}. {c.name}</span><span className="font-mono-data text-emerald-300">{c.score}%</span></div>
                <div className="h-2 rounded-full bg-white/8 overflow-hidden"><motion.div className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400" initial={{ width: 0 }} animate={{ width: `${c.score}%` }} transition={{ duration: 0.8 }} /></div>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-4 text-center">
            <p className="font-display text-2xl font-extrabold text-cyan-300">{profile.total_paths || careers.length}</p>
            <p className="text-xs text-slate-400">career paths match your profile</p>
          </div>
        </div>
      </div>

      {/* Career paths — free unlocked */}
      <div className="mt-6">
        <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><TrendingUp className="h-5 w-5 text-cyan-400" /> Your top career matches</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          {careers.slice(0, unlocked).map((c) => (
            <div key={c.title} data-testid="career-path-card" className="rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.04] p-5">
              <div className="flex items-center justify-between"><p className="font-display font-semibold text-white">{c.title}</p><span className="font-mono-data text-cyan-300">{c.match}%</span></div>
              <p className="mt-1 text-xs text-slate-400">{c.why}</p>
              {c.skills && c.skills.length > 0 && <div className="mt-2 flex flex-wrap gap-1">{c.skills.slice(0, 3).map((s) => <span key={s} className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-slate-300">{s}</span>)}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Premium lock */}
      {locked > 0 && (
        <div data-testid="premium-lock" className="mt-6 relative overflow-hidden rounded-2xl border border-amber-400/25 bg-gradient-to-br from-[#2a220f] to-[#131C2E] p-8 text-center">
          <Lock className="mx-auto h-8 w-8 text-amber-400 mb-3" />
          <h3 className="font-display text-2xl font-bold">Unlock your complete Career Intelligence Report</h3>
          <p className="mt-2 text-slate-300 max-w-lg mx-auto">We've found <span className="font-bold text-amber-300">{locked} more career paths</span> plus recommended colleges, entrance exams, a personalized roadmap, salary outlook and a downloadable PDF.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs text-slate-400">
            {["All matched careers", "Colleges & exams", "Career roadmap", "Salary outlook", "PDF report", "Parent insights"].map((f) => (
              <span key={f} className="inline-flex items-center gap-1 rounded-full bg-white/5 px-3 py-1"><Check className="h-3 w-3 text-emerald-400" /> {f}</span>
            ))}
          </div>
          <button data-testid="unlock-premium-btn" onClick={onUpgrade}
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-8 py-3.5 text-base font-bold text-[#1a1206] transition-transform hover:scale-105">
            <Crown className="h-5 w-5" /> Unlock Premium Report
          </button>
        </div>
      )}

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <button data-testid="assessment-go-dashboard-btn" onClick={onGo}
          className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-8 py-3.5 text-base font-bold text-[#041016] transition-transform hover:scale-105 glow-cyan">
          Go to Dashboard <ArrowRight className="h-5 w-5" />
        </button>
        <ShareReport />
        <button data-testid="assessment-retake-btn" onClick={onRetake}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-3.5 text-base font-bold text-slate-200 transition-all hover:border-cyan-400/50 hover:text-white">
          <RotateCcw className="h-5 w-5" /> Retake assessment
        </button>
      </div>
      <p className="mt-6 text-center text-[11px] text-slate-500 flex items-center justify-center gap-1"><Award className="h-3 w-3" /> AI-generated guidance — not a guaranteed career outcome.</p>
    </div>
  );
};
